"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SessionTimeoutWarningModal from "@/components/common/SessionTimeoutWarningModal";
import { useAuthContext } from "@/contexts/common/AuthContextProvider";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import {
  SESSION_EXPIRED_EVENT,
  clearCachesAndLogout,
  registerSessionExpiredListener,
} from "@/utils/common/logout";

// Second warning threshold (in seconds) - shown after first warning is dismissed
const SECOND_WARNING_THRESHOLD = 120; // 2 minutes

// Grace period (in seconds) given to the user to save work when a background
// token refresh detects the session is dead. This countdown is shown in the
// warning modal.
const SESSION_EXPIRED_GRACE_SECONDS = 120; // 2 minutes

/**
 * Context value for session timeout.
 */
export interface SessionTimeoutContextValue {
  /** Whether the session timeout config has been loaded */
  isLoaded: boolean;
  /** Idle timeout in minutes (0 = indefinite) */
  idleTimeoutMinutes: number;
  /** Unix timestamp when absolute session expires (null = indefinite) */
  sessionExpiresAt: number | null;
  /** Reset the idle timer (call on important user actions) */
  resetIdleTimer: () => void;
  /** Seconds remaining until idle timeout */
  idleSecondsRemaining: number;
  /** Seconds remaining until absolute session expires */
  absoluteSecondsRemaining: number | null;
  /** Full decoded claims (debug only) */
  claims?: any;
}

const SessionTimeoutContext = createContext<SessionTimeoutContextValue | undefined>(undefined);

/**
 * Hook to access session timeout context.
 * Use this to manually reset the idle timer on important user actions
 * that might not trigger standard activity events.
 */
export function useSessionTimeoutContext(): SessionTimeoutContextValue {
  const context = useContext(SessionTimeoutContext);
  if (!context) {
    throw new Error("useSessionTimeoutContext must be used within a SessionTimeoutProvider");
  }
  return context;
}

interface SessionTimeoutProviderProps {
  children: ReactNode;
  /** Seconds before idle timeout to show warning (default: 120 = 2 minutes) */
  warningBeforeIdleTimeout?: number;
  /** Seconds before absolute timeout to show warning (default: 300 = 5 minutes) */
  warningBeforeAbsoluteTimeout?: number;
  /** Custom logout handler (if not provided, redirects to /auth/logout) */
  onLogout?: () => void;
}

/**
 * Provider that manages session timeout with both idle and absolute timeout support.
 *
 * Features:
 * - Automatically enabled when user is authenticated (via useAuthContext)
 * - Tracks user activity to detect inactivity
 * - Shows warning modal before idle timeout
 * - Tracks absolute session expiration
 * - Auto-logs out when either timeout is reached
 *
 * Note: Must be placed after AuthContextProvider in the provider tree.
 *
 * Usage:
 * ```tsx
 * <AuthContextProvider>
 *   <SessionTimeoutProvider>
 *     {children}
 *   </SessionTimeoutProvider>
 * </AuthContextProvider>
 * ```
 */
export function SessionTimeoutProvider({
  children,
  warningBeforeIdleTimeout = 120,
  warningBeforeAbsoluteTimeout = 300,
  onLogout,
}: Readonly<SessionTimeoutProviderProps>) {
  // Get auth state to determine if session timeout should be enabled
  const { isAuthenticated, isLoading, renewToken } = useAuthContext();

  // Only enable session timeout when user is authenticated and auth has loaded
  const enabled = isAuthenticated && !isLoading;

  const sessionTimeout = useSessionTimeout({
    enabled,
    warningBeforeIdleTimeout,
    warningBeforeAbsoluteTimeout,
    onLogout,
  });

  // Track which warning thresholds have been dismissed
  // Once dismissed at a threshold, won't show again until crossing the next threshold
  const [dismissedThresholds, setDismissedThresholds] = useState<Set<number>>(new Set());

  // Forced session-expired state: set when a background token refresh detects
  // the session is dead. Shows a warning modal with a grace countdown instead
  // of immediately redirecting to logout.
  //
  // "showForceExpiredModal" controls modal visibility (user can dismiss).
  // "forceExpiredCountdownActive" owns the ticking countdown — dismissing the
  // modal does NOT stop it; only the auto-logout at 0 or an explicit Logout
  // click does. "forceExpiredActiveRef" deduplicates synchronous re-fires of
  // the event before state updates have flushed.
  const [showForceExpiredModal, setShowForceExpiredModal] = useState(false);
  const [forceExpiredSecondsRemaining, setForceExpiredSecondsRemaining] = useState(0);
  const [forceExpiredCountdownActive, setForceExpiredCountdownActive] = useState(false);
  const forceExpiredActiveRef = useRef(false);

  // Register this provider as the session-expired listener so that
  // notifySessionExpired() knows to dispatch the event rather than
  // falling back to an immediate logout.
  useEffect(() => {
    return registerSessionExpiredListener();
  }, []);

  // Listen for the gail:session-expired event dispatched by the token refresh
  // system when it discovers the session is irrecoverably dead. The handler
  // only flips state — the countdown interval lives in its own effect below
  // so its lifetime is bound to forceExpiredCountdownActive, not to this
  // listener's effect (which would cause the interval to die during StrictMode
  // dev double-mount or any future dep change to this effect).
  useEffect(() => {
    const handleSessionExpired = () => {
      if (forceExpiredActiveRef.current) return;
      forceExpiredActiveRef.current = true;
      setShowForceExpiredModal(true);
      setForceExpiredSecondsRemaining(SESSION_EXPIRED_GRACE_SECONDS);
      setForceExpiredCountdownActive(true);
    };

    globalThis.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => {
      globalThis.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);

  // Drives the auto-logout countdown for the force-expired grace period.
  // Tied to forceExpiredCountdownActive, not to the listener effect, so this
  // ticker survives dev StrictMode's double-mount and any future change to
  // the listener effect's deps.
  useEffect(() => {
    if (!forceExpiredCountdownActive) return;
    const id = setInterval(() => {
      setForceExpiredSecondsRemaining((prev) => {
        if (prev <= 1) {
          clearCachesAndLogout({ saveReturnUrl: true });
          setForceExpiredCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [forceExpiredCountdownActive]);

  // Reset dismissed thresholds when session changes (new login)
  useEffect(() => {
    setDismissedThresholds(new Set());
  }, [sessionTimeout.sessionExpiresAt]);

  // Handler for "Stay Logged In" button - refreshes token and resets idle timer.
  // Note: This does NOT extend the absolute session timeout per OWASP guidelines.
  const handleStayLoggedIn = useCallback(() => {
    void renewToken();
    sessionTimeout.refreshTokenAndResetIdle();
  }, [renewToken, sessionTimeout.refreshTokenAndResetIdle]);

  // Derive warning thresholds from the configurable prop
  // First threshold is from prop, second is always 2 minutes (if first is larger)
  const warningThresholds = useMemo(() => {
    const thresholds = [warningBeforeAbsoluteTimeout];
    if (warningBeforeAbsoluteTimeout > SECOND_WARNING_THRESHOLD) {
      thresholds.push(SECOND_WARNING_THRESHOLD);
    }
    return thresholds;
  }, [warningBeforeAbsoluteTimeout]);

  // Determine current threshold based on remaining time
  // Returns the smallest threshold that secondsRemaining is at or below
  const getCurrentThreshold = useCallback(
    (secondsRemaining: number | null): number | null => {
      if (secondsRemaining === null) return null;
      // Find the smallest threshold that the remaining time is at or below
      // e.g., at 100s remaining with thresholds [300, 120]: returns 120
      // e.g., at 250s remaining with thresholds [300, 120]: returns 300
      let currentThreshold: number | null = null;
      for (const threshold of warningThresholds) {
        if (secondsRemaining <= threshold) {
          // Keep the smallest matching threshold
          if (currentThreshold === null || threshold < currentThreshold) {
            currentThreshold = threshold;
          }
        }
      }
      return currentThreshold;
    },
    [warningThresholds]
  );

  const currentThreshold = getCurrentThreshold(sessionTimeout.absoluteSecondsRemaining);

  // Handler for dismissing absolute warning - marks current threshold as dismissed
  const handleDismissAbsoluteWarning = useCallback(() => {
    if (currentThreshold !== null) {
      setDismissedThresholds((prev) => new Set([...prev, currentThreshold]));
    }
  }, [currentThreshold]);

  const handleLogout = useCallback(async () => {
    // Stop the force-expired countdown so it doesn't race with this explicit
    // logout. Without this, the ticker could reach 0 and call
    // clearCachesAndLogout first (setting _isLoggingOut=true), making this
    // button click silently a no-op. The countdown effect's cleanup clears
    // the actual interval.
    setForceExpiredCountdownActive(false);

    if (onLogout) {
      onLogout();
    } else {
      await clearCachesAndLogout({ saveReturnUrl: true });
    }
  }, [onLogout]);

  const contextValue = useMemo<SessionTimeoutContextValue>(
    () => ({
      isLoaded: sessionTimeout.isLoaded,
      idleTimeoutMinutes: sessionTimeout.idleTimeoutMinutes,
      sessionExpiresAt: sessionTimeout.sessionExpiresAt,
      resetIdleTimer: sessionTimeout.resetIdleTimer,
      idleSecondsRemaining: sessionTimeout.idleSecondsRemaining,
      absoluteSecondsRemaining: sessionTimeout.absoluteSecondsRemaining,
      claims: sessionTimeout.claims,
    }),
    [
      sessionTimeout.isLoaded,
      sessionTimeout.idleTimeoutMinutes,
      sessionTimeout.sessionExpiresAt,
      sessionTimeout.resetIdleTimer,
      sessionTimeout.idleSecondsRemaining,
      sessionTimeout.absoluteSecondsRemaining,
      sessionTimeout.claims,
    ]
  );

  // Show absolute warning if we're at a threshold that hasn't been dismissed
  const showAbsoluteModal =
    sessionTimeout.showAbsoluteWarning &&
    currentThreshold !== null &&
    !dismissedThresholds.has(currentThreshold);

  // Force-expired takes highest priority (background refresh detected dead session)
  // Absolute warning comes next, then idle warning
  const showAbsoluteModalFinal = showAbsoluteModal && !showForceExpiredModal;
  const showIdleModal =
    sessionTimeout.showIdleWarning && !showAbsoluteModalFinal && !showForceExpiredModal;

  // Handler for dismissing the force-expired warning — hides the modal but the
  // countdown keeps running in the background and will log out when it hits 0
  const handleDismissForceExpired = useCallback(() => {
    setShowForceExpiredModal(false);
  }, []);

  return (
    <SessionTimeoutContext.Provider value={contextValue}>
      {children}

      {/* Idle timeout warning modal - hidden when absolute/force-expired warning is active */}
      <SessionTimeoutWarningModal
        open={showIdleModal}
        secondsRemaining={sessionTimeout.idleSecondsRemaining}
        timeoutType="idle"
        onExtend={handleStayLoggedIn}
        onLogout={handleLogout}
      />

      {/* Absolute timeout warning modal - can be dismissed temporarily */}
      <SessionTimeoutWarningModal
        open={showAbsoluteModalFinal}
        secondsRemaining={sessionTimeout.absoluteSecondsRemaining ?? 0}
        timeoutType="absolute"
        onLogout={handleLogout}
        onDismiss={handleDismissAbsoluteWarning}
      />

      {/* Force-expired warning modal — shown when background refresh detected dead session. */}
      <SessionTimeoutWarningModal
        open={showForceExpiredModal}
        secondsRemaining={forceExpiredSecondsRemaining}
        timeoutType="session_expired"
        onLogout={handleLogout}
        onDismiss={handleDismissForceExpired}
      />
    </SessionTimeoutContext.Provider>
  );
}
